import { type Styles } from '@adornis/ass/style.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { type DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { LevumiSubject } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import { type LevumiAnswer } from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { colors } from '@chancenwerk/design/theme.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import check from '../../assets/icons/Check.svg';
import cross from '../../assets/icons/Cross.svg';

@customElement('campus-dialog-show-levumi-answers')
export class CampusDialogShowLevumiAnswers extends ChromiumDialog<void> {
  static override get element_name() {
    return 'campus-dialog-show-levumi-answers';
  }

  override styles() {
    return [
      ...super.styles(),
      {
        '.container:nth-child(2n) > cr-flex': { background: `${colors.chancen_green_primary}32` },
      } satisfies Styles,
    ];
  }

  @state() subject = '';
  @state() correctAnswers: LevumiAnswer[] = [];
  @state() wrongAnswers: LevumiAnswer[] = [];

  override heading = '';

  override content() {
    return html`<cr-flex space="md" padding="md">
      ${this.subject === LevumiSubject.GERMAN ? this._showGermanAnswerGrid() : this._showMathAnswerGrid()}
    </cr-flex>`;
  }

  private _showGermanAnswerGrid() {
    return html`
      <cr-flex horizontal center ${css({ width: '68vw' })} space-between>
        ${this.correctAnswers.length
          ? html`
              <cr-flex ${css({ height: '60vh', overflow: 'auto', width: this.wrongAnswers.length ? '50%' : '100%' })}>
                <cr-grid columns="0.1fr 1fr 0.5fr 0.5fr">
                  ${repeat(
                    this.correctAnswers,
                    answer => answer.question,
                    answer =>
                      html`
                        <div class="container" ${css({ display: 'contents' })}>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${this._getIcon(answer.answerCorrect ?? false)}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${answer.question}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text></cr-text>
                              ${!answer.answerCorrect ? 'Richtige Antwort: ' + answer.correctAnswer : ''}
                            </cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>Antwort: ${answer.studentAnswer}</cr-text>
                          </cr-flex>
                        </div>
                      `,
                  )}
                </cr-grid>
              </cr-flex>
            `
          : nothing}
        ${this.wrongAnswers.length
          ? html`
              <cr-flex ${css({ height: '60vh', overflow: 'auto', width: this.correctAnswers.length ? '50%' : '100%' })}>
                <cr-grid columns="0.1fr 1fr 0.5fr 0.5fr">
                  ${repeat(
                    this.wrongAnswers,
                    answer => answer.question,
                    answer =>
                      html`
                        <div class="container" ${css({ display: 'contents' })}>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${this._getIcon(answer.answerCorrect ?? false)}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${answer.question}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>
                              ${!answer.answerCorrect ? 'Richtige Antwort: ' + answer.correctAnswer : ''}
                            </cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>Antwort: ${answer.studentAnswer}</cr-text>
                          </cr-flex>
                        </div>
                      `,
                  )}
                </cr-grid>
              </cr-flex>
            `
          : nothing}
      </cr-flex>
    `;
  }

  private _showMathAnswerGrid() {
    return html`
      <cr-flex horizontal center ${css({ width: '68vw' })} space-between>
        ${this.correctAnswers.length
          ? html`
              <cr-flex ${css({ height: '60vh', overflow: 'auto', width: this.wrongAnswers.length ? '50%' : '100%' })}>
                <cr-grid columns="0.1fr 1fr 0.5fr">
                  ${repeat(
                    this.correctAnswers,
                    answer => answer.question,
                    answer =>
                      html`
                        <div class="container" ${css({ display: 'contents' })}>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${this._getIcon(answer.answerCorrect ?? false)}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${answer.question}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>Antwort: ${answer.studentAnswer}</cr-text>
                          </cr-flex>
                        </div>
                      `,
                  )}
                </cr-grid>
              </cr-flex>
            `
          : nothing}
        ${this.wrongAnswers.length
          ? html`
              <cr-flex ${css({ height: '60vh', overflow: 'auto', width: this.correctAnswers.length ? '50%' : '100%' })}>
                <cr-grid columns="0.1fr 1fr 0.5fr">
                  ${repeat(
                    this.wrongAnswers,
                    answer => answer.question,
                    answer =>
                      html`
                        <div class="container" ${css({ display: 'contents' })}>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${this._getIcon(answer.answerCorrect ?? false)}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>${answer.question}</cr-text>
                          </cr-flex>
                          <cr-flex padding="sm" center crossaxis-center>
                            <cr-text>Antwort: ${answer.studentAnswer}</cr-text>
                          </cr-flex>
                        </div>
                      `,
                  )}
                </cr-grid>
              </cr-flex>
            `
          : nothing}
      </cr-flex>
    `;
  }

  private _getIcon(correct: boolean) {
    return html`<cr-icon type="img" src=${correct ? check : cross}></cr-icon>`;
  }

  override getInnerWrapper(options: Required<DialogOptions>): HTMLDivElement {
    const wrapperElem = super.getInnerWrapper(options);
    if (!options.draggable) {
      wrapperElem.style.setProperty('min-width', '70vw');
    }
    return wrapperElem;
  }
}
